import {
  prisma,
  reportingPrisma,
  Prisma,
  ParticipantGroupType,
  User,
  EntityModel,
  ConnectionStatus,
} from "client";
import { getBookmarkedEntityIds } from "../bookmark";
import {
  buildCompanySearchCards,
  companySearchCardNeedsProfile,
  mapCompanyDocuments,
  mapCompanyProducts,
  mapCompanyTeamMembers,
  mapCompanyToParticipantItem,
  mapCompanyVideos,
  pickCompanyContactProfile,
  type CompanyParticipantItem,
  type CompanyProfileLike,
  type CompanyTranslationLike,
} from "./companies.pure";
import { parseCompanyListType } from "./companies-type.pure";
import {
  buildCompanySearchCollectionWhere,
  companyCursorPage,
  companyListPage,
  emptyCompanyListPage,
  escapeCompanySearchTerm,
  normalizeCompanyCursor,
  normalizeCompanyPagination,
  normalizeCompanySearchQuery,
  pageFromCompanyIdSides,
  planCompanyIdWindow,
} from "./companies-pagination.pure";

/**
 * List hydration. Contact profiles are loaded only for the page: `team.profiles`
 * is what `pickCompanyContactProfile` reads, and the card does not return the
 * team. Group color and name come from the participant-group query.
 */
const companyListInclude = (eventId: string) => ({
  translations: {
    include: {
      language: {
        select: { code: true },
      },
    },
  },
  networking: true,
  team: {
    select: {
      email: true,
      profiles: {
        where: { eventId },
        orderBy: { createdAt: "desc" as const },
        take: 1,
        select: {
          id: true,
          eventId: true,
          name: true,
          email: true,
          phoneNumber: true,
          countryCode: true,
          jobTitle: true,
          company: true,
          country: true,
          city: true,
          state: true,
          gender: true,
          age: true,
          bio: true,
          image: true,
          socialMedia: true,
          userId: true,
          extraData: true,
          createdAt: true,
          updatedAt: true,
        },
      },
    },
  },
});

const companyCollectionWhere = (
  eventId: string,
  groupIds: string[]
): Prisma.ExhibitorWhereInput | null => {
  if (!groupIds.length) return null;
  return {
    eventId,
    participantGroupId: { in: groupIds },
  };
};

const SEARCH_ID_CHUNK = 500;

async function inChunks<T>(
  ids: string[],
  load: (chunk: string[]) => Promise<T[]>
) {
  if (!ids.length) return [] as T[];
  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += SEARCH_ID_CHUNK) {
    chunks.push(ids.slice(index, index + SEARCH_ID_CHUNK));
  }
  const parts = await Promise.all(chunks.map((chunk) => load(chunk)));
  return parts.flat();
}

type SearchCompanyRow = {
  id: string;
  email: string | null;
  teamIds: string[];
};

function rawMongoId(value: unknown): string | null {
  if (typeof value === "string" && value) return value;
  if (value && typeof value === "object" && "$oid" in value) {
    const oid = (value as { $oid?: unknown }).$oid;
    return typeof oid === "string" ? oid : null;
  }
  return null;
}

type MissingSearchCardRow = {
  id: string;
  email: string | null;
  teamIds: string[];
};

const SEARCH_CARD_BACKFILL_BATCH = 200;

function readMissingSearchCardRow(row: unknown): MissingSearchCardRow | null {
  if (!row || typeof row !== "object") return null;
  const record = row as { _id?: unknown; email?: unknown; teamIds?: unknown };
  const id = rawMongoId(record._id);
  if (!id) return null;
  const teamIds = Array.isArray(record.teamIds)
    ? record.teamIds.flatMap((value) => {
        const userId = rawMongoId(value);
        return userId ? [userId] : [];
      })
    : [];
  return {
    id,
    email: typeof record.email === "string" ? record.email : null,
    teamIds,
  };
}

function missingSearchCardMatch(eventId: string, groupIds: string[]) {
  return {
    eventId: { $oid: eventId },
    participantGroupId: { $in: groupIds.map((id) => ({ $oid: id })) },
    $or: [{ searchCards: null }, { searchCards: { $size: 0 } }],
  };
}

async function loadCompaniesMissingSearchCards(
  collection: "exhibitor" | "sponsor",
  eventId: string,
  groupIds: string[]
) {
  if (!groupIds.length) return [] as MissingSearchCardRow[];
  const pipeline = [
    { $match: missingSearchCardMatch(eventId, groupIds) },
    { $limit: SEARCH_CARD_BACKFILL_BATCH },
    { $project: { _id: 1, email: 1, teamIds: 1 } },
  ];
  const raw =
    collection === "exhibitor"
      ? await reportingPrisma.exhibitor.aggregateRaw({ pipeline })
      : await reportingPrisma.sponsor.aggregateRaw({ pipeline });
  const rows = Array.isArray(raw) ? raw : [];
  return rows.flatMap((row) => {
    const parsed = readMissingSearchCardRow(row);
    return parsed ? [parsed] : [];
  });
}

/**
 * Existing companies have no `searchCards`. Fill them from translation name +
 * email (and the contact profile only when the card still falls back) so the
 * paged find can filter on the company document. Bounded batches; steady-state
 * search stops after a single empty probe.
 */
async function backfillMissingSearchCards(
  collection: "exhibitor" | "sponsor",
  eventId: string,
  groupIds: string[]
) {
  const seen = new Set<string>();
  for (let round = 0; round < 80; round += 1) {
    const rows = await loadCompaniesMissingSearchCards(
      collection,
      eventId,
      groupIds
    );
    if (!rows.length) return;
    if (rows.every((row) => seen.has(row.id))) return;
    for (const row of rows) seen.add(row.id);

    const translations = await loadCompanyTranslationNames(
      collection === "exhibitor" ? rows.map((row) => row.id) : [],
      collection === "sponsor" ? rows.map((row) => row.id) : []
    );
    const needing = rows.filter(
      (row) =>
        row.teamIds.length > 0 &&
        companySearchCardNeedsProfile(row.email, translations.get(row.id))
    );
    const contacts = await loadSearchContactProfiles(eventId, needing);
    const cardsById = new Map(
      rows.map((row) => [
        row.id,
        buildCompanySearchCards({
          email: row.email,
          translations: translations.get(row.id),
          profile: contacts.get(row.id),
        }),
      ])
    );

    const updates = rows.map((row) => ({
      q: { _id: { $oid: row.id } },
      u: { $set: { searchCards: cardsById.get(row.id) ?? [] } },
    }));
    await prisma.$runCommandRaw({
      update: collection === "exhibitor" ? "Exhibitor" : "Sponsor",
      updates,
      ordered: false,
    });
  }
}

const searchCardBackfill = new Map<string, Promise<void>>();

async function ensureCompanySearchCards(args: {
  eventId: string;
  exhibitorGroupIds: string[];
  sponsorGroupIds: string[];
}) {
  const key = [
    args.eventId,
    args.exhibitorGroupIds.join(","),
    args.sponsorGroupIds.join(","),
  ].join("|");
  const pending = searchCardBackfill.get(key);
  if (pending) return pending;

  const run = (async () => {
    await Promise.all([
      backfillMissingSearchCards(
        "exhibitor",
        args.eventId,
        args.exhibitorGroupIds
      ),
      backfillMissingSearchCards(
        "sponsor",
        args.eventId,
        args.sponsorGroupIds
      ),
    ]);
  })().finally(() => {
    searchCardBackfill.delete(key);
  });
  searchCardBackfill.set(key, run);
  return run;
}

async function loadCompanyTranslationNames(
  exhibitorIds: string[],
  sponsorIds: string[]
) {
  const [exhibitorTranslations, sponsorTranslations] = await Promise.all([
    inChunks(exhibitorIds, (chunk) =>
      reportingPrisma.exhibitorTranslations.findMany({
        where: { exhibitorId: { in: chunk } },
        select: { id: true, exhibitorId: true, name: true, languageId: true },
      })
    ),
    inChunks(sponsorIds, (chunk) =>
      reportingPrisma.sponsorTranslations.findMany({
        where: { sponsorId: { in: chunk } },
        select: { id: true, sponsorId: true, name: true, languageId: true },
      })
    ),
  ]);
  exhibitorTranslations.sort((left, right) => left.id.localeCompare(right.id));
  sponsorTranslations.sort((left, right) => left.id.localeCompare(right.id));

  const languageIds = [
    ...new Set(
      [...exhibitorTranslations, ...sponsorTranslations].map(
        (row) => row.languageId
      )
    ),
  ];
  const languages = await inChunks(languageIds, (chunk) =>
    reportingPrisma.language.findMany({
      where: { id: { in: chunk } },
      select: { id: true, code: true },
    })
  );
  const codeById = new Map(
    languages.map((language) => [language.id, language.code])
  );
  const byCompany = new Map<string, CompanyTranslationLike[]>();
  const add = (companyId: string, name: string, languageId: string) => {
    const list = byCompany.get(companyId) ?? [];
    list.push({
      name,
      language: { code: codeById.get(languageId) ?? null },
    });
    byCompany.set(companyId, list);
  };
  for (const row of exhibitorTranslations) {
    add(row.exhibitorId, row.name, row.languageId);
  }
  for (const row of sponsorTranslations) {
    add(row.sponsorId, row.name, row.languageId);
  }
  return byCompany;
}

async function loadSearchContactProfiles(
  eventId: string,
  rows: SearchCompanyRow[]
) {
  const profilesByCompany = new Map<string, CompanyProfileLike | undefined>();
  if (!rows.length) return profilesByCompany;

  const userIds = [...new Set(rows.flatMap((row) => row.teamIds))];
  const users = await inChunks(userIds, (chunk) =>
    reportingPrisma.user.findMany({
      where: { id: { in: chunk } },
      select: { id: true, email: true },
    })
  );
  const eventProfiles = await inChunks(userIds, (chunk) =>
    reportingPrisma.profile.findMany({
      where: { eventId, userId: { in: chunk } },
      select: {
        userId: true,
        company: true,
        name: true,
        email: true,
        eventId: true,
        createdAt: true,
      },
    })
  );
  const newestByUser = new Map<string, (typeof eventProfiles)[number]>();
  for (const profile of eventProfiles) {
    if (!profile.userId) continue;
    const current = newestByUser.get(profile.userId);
    if (!current || profile.createdAt > current.createdAt) {
      newestByUser.set(profile.userId, profile);
    }
  }
  const userById = new Map(users.map((user) => [user.id, user]));

  for (const row of rows) {
    const team = row.teamIds.flatMap((userId) => {
      const user = userById.get(userId);
      if (!user) return [];
      const profile = newestByUser.get(userId);
      return [
        {
          email: user.email,
          profiles: profile ? [profile] : [],
        },
      ];
    });
    profilesByCompany.set(
      row.id,
      pickCompanyContactProfile(team, row.email, eventId)
    );
  }
  return profilesByCompany;
}

/**
 * Search uses the same reporting id-window as the unfiltered list. The filter
 * is an embedded `searchCards` elemMatch, so Mongo applies `limit` while
 * scanning `{ eventId, _id }` and does not load every matching id first.
 * `boothName` is not part of `searchCards`.
 */
async function loadCompanySearchWindow(args: {
  eventId: string;
  lang: string;
  query: string;
  exhibitorGroupIds: string[];
  sponsorGroupIds: string[];
  page: number;
  take: number;
  cursor?: string;
}): Promise<CompanyIdWindow> {
  await ensureCompanySearchCards({
    eventId: args.eventId,
    exhibitorGroupIds: args.exhibitorGroupIds,
    sponsorGroupIds: args.sponsorGroupIds,
  });
  const textContains = escapeCompanySearchTerm(args.query);
  return loadMergedCompanyWindow({
    exhibitorWhere: buildCompanySearchCollectionWhere({
      eventId: args.eventId,
      groupIds: args.exhibitorGroupIds,
      lang: args.lang,
      textContains,
    }),
    sponsorWhere: buildCompanySearchCollectionWhere({
      eventId: args.eventId,
      groupIds: args.sponsorGroupIds,
      lang: args.lang,
      textContains,
    }),
    page: args.page,
    take: args.take,
    cursor: args.cursor,
  });
}

const countCompanies = (
  collection: "exhibitor" | "sponsor",
  where: Prisma.ExhibitorWhereInput
) =>
  collection === "exhibitor"
    ? reportingPrisma.exhibitor.count({ where })
    : reportingPrisma.sponsor.count({
        where: where as Prisma.SponsorWhereInput,
      });

const findCompanyIds = (
  collection: "exhibitor" | "sponsor",
  where: Prisma.ExhibitorWhereInput,
  order: "asc" | "desc",
  take: number
) => {
  if (take <= 0) return Promise.resolve([] as { id: string }[]);
  if (collection === "exhibitor") {
    return reportingPrisma.exhibitor.findMany({
      where,
      orderBy: { id: order },
      take,
      select: { id: true },
    });
  }
  return reportingPrisma.sponsor.findMany({
    where: where as Prisma.SponsorWhereInput,
    orderBy: { id: order },
    take,
    select: { id: true },
  });
};

const loadCompanyNetworkState = async (
  eventId: string,
  sessionId: string,
  companyIds: string[]
) => {
  if (!companyIds.length) {
    return {
      bookmarkedIds: new Set<string>(),
      connectionStatus: new Map<string, ConnectionStatus>(),
    };
  }

  const [bookmarks, connectionRequests] = await Promise.all([
    reportingPrisma.bookmark.findMany({
      where: {
        userId: sessionId,
        eventId,
        entityModel: EntityModel.PROFILE,
        entityId: { in: companyIds },
      },
      select: { entityId: true },
    }),
    reportingPrisma.connectionRequest.findMany({
      where: {
        eventId,
        status: { not: ConnectionStatus.REJECTED },
        OR: [
          { senderId: sessionId, receiverId: { in: companyIds } },
          { receiverId: sessionId, senderId: { in: companyIds } },
        ],
      },
      select: { senderId: true, receiverId: true, status: true },
      orderBy: { createdAt: "desc" },
    }),
  ]);

  const connectionStatus = new Map<string, ConnectionStatus>();
  for (const request of connectionRequests) {
    const peerId =
      request.senderId === sessionId ? request.receiverId : request.senderId;
    if (!connectionStatus.has(peerId)) {
      connectionStatus.set(peerId, request.status);
    }
  }

  return {
    bookmarkedIds: new Set(bookmarks.map((bookmark) => bookmark.entityId)),
    connectionStatus,
  };
};

type CompanyIdWindow = {
  ids: string[];
  exhibitorIds: Set<string>;
  total: number;
  hasNextPage: boolean;
  nextCursor: string | null;
};

/**
 * Page of company ids across Exhibitor and Sponsor.
 *
 * The two collections stay sorted by id and are merged after each side has
 * been limited. Page 1 and cursor pages read `limit` (cursor: `limit + 1`)
 * ids per side. Deeper offset pages read from the nearer end, so the last
 * page stays the same size as the first. Counts are indexed totals for
 * `pagination.total`; they are not a second copy of the page.
 */
async function loadMergedCompanyWindow(args: {
  exhibitorWhere: Prisma.ExhibitorWhereInput | null;
  sponsorWhere: Prisma.ExhibitorWhereInput | null;
  page: number;
  take: number;
  cursor?: string;
}): Promise<CompanyIdWindow> {
  const { exhibitorWhere, sponsorWhere, page, take, cursor } = args;
  const countSide = (
    where: Prisma.ExhibitorWhereInput | null,
    collection: "exhibitor" | "sponsor"
  ) => (where ? countCompanies(collection, where) : Promise.resolve(0));

  if (cursor || page === 1) {
    const idBound = cursor ? { id: { gt: cursor } } : {};
    const windowTake = cursor ? take + 1 : take;
    const exhibitorPageWhere = exhibitorWhere
      ? { ...exhibitorWhere, ...idBound }
      : null;
    const sponsorPageWhere = sponsorWhere
      ? { ...sponsorWhere, ...idBound }
      : null;

    const [exhibitorCount, sponsorCount, exhibitorRows, sponsorRows] =
      await Promise.all([
        countSide(exhibitorWhere, "exhibitor"),
        countSide(sponsorWhere, "sponsor"),
        exhibitorPageWhere
          ? findCompanyIds("exhibitor", exhibitorPageWhere, "asc", windowTake)
          : Promise.resolve([]),
        sponsorPageWhere
          ? findCompanyIds("sponsor", sponsorPageWhere, "asc", windowTake)
          : Promise.resolve([]),
      ]);
    const total = exhibitorCount + sponsorCount;
    const exhibitorIds = new Set(exhibitorRows.map((row) => row.id));

    if (cursor) {
      const cursorPage = companyCursorPage(exhibitorRows, sponsorRows, take);
      return {
        ids: cursorPage.page.map((row) => row.id),
        exhibitorIds,
        total,
        hasNextPage: cursorPage.hasNextPage,
        nextCursor: cursorPage.nextCursor,
      };
    }

    const merged = pageFromCompanyIdSides(exhibitorRows, sponsorRows, {
      direction: "forward",
      fetchPerSide: take,
      skipInMerged: 0,
      limit: take,
    });
    const hasNextPage = total > take;
    return {
      ids: merged.map((row) => row.id),
      exhibitorIds,
      total,
      hasNextPage,
      nextCursor: hasNextPage ? merged[merged.length - 1]?.id ?? null : null,
    };
  }

  const [exhibitorCount, sponsorCount] = await Promise.all([
    countSide(exhibitorWhere, "exhibitor"),
    countSide(sponsorWhere, "sponsor"),
  ]);
  const total = exhibitorCount + sponsorCount;
  const offset = (page - 1) * take;
  const plan = planCompanyIdWindow({ total, offset, limit: take });
  if (!plan) {
    return {
      ids: [],
      exhibitorIds: new Set(),
      total,
      hasNextPage: false,
      nextCursor: null,
    };
  }

  const order = plan.direction === "backward" ? "desc" : "asc";
  const [exhibitorRows, sponsorRows] = await Promise.all([
    exhibitorWhere
      ? findCompanyIds("exhibitor", exhibitorWhere, order, plan.fetchPerSide)
      : Promise.resolve([]),
    sponsorWhere
      ? findCompanyIds("sponsor", sponsorWhere, order, plan.fetchPerSide)
      : Promise.resolve([]),
  ]);
  const merged = pageFromCompanyIdSides(exhibitorRows, sponsorRows, plan);
  const hasNextPage = offset + merged.length < total;
  return {
    ids: merged.map((row) => row.id),
    exhibitorIds: new Set(exhibitorRows.map((row) => row.id)),
    total,
    hasNextPage,
    nextCursor: hasNextPage ? merged[merged.length - 1]?.id ?? null : null,
  };
}

/**
 * Companies tab for an event.
 *
 * Reads go through `reportingPrisma` so a large event cannot open the
 * transactional circuit. The page window is applied before translations,
 * networking, contact profiles, bookmarks, or connection requests are loaded.
 */
export const getCompaniesByEventWithNetworkData = async (
  eventId: string,
  lang: string = "en",
  query?: string,
  session?: User,
  page: number = 1,
  take: number = 10,
  type?: string | null,
  cursor?: string
) => {
  try {
    const pagination = normalizeCompanyPagination(page, take);
    const safeCursor = normalizeCompanyCursor(cursor);
    const listType = parseCompanyListType(type);
    const wantedTypes: ParticipantGroupType[] = [];
    if (!listType || listType === ParticipantGroupType.EXHIBITORS) {
      wantedTypes.push(ParticipantGroupType.EXHIBITORS);
    }
    if (!listType || listType === ParticipantGroupType.SPONSORS) {
      wantedTypes.push(ParticipantGroupType.SPONSORS);
    }

    const groups = await reportingPrisma.participantGroup.findMany({
      where: { eventId, type: { in: wantedTypes } },
      select: {
        id: true,
        type: true,
        color: true,
        translations: {
          where: { language: { code: lang } },
          select: { name: true },
        },
      },
    });

    const hasPreviousPage = pagination.page > 1 || Boolean(safeCursor);
    if (!groups.length) {
      return emptyCompanyListPage<CompanyParticipantItem>(
        pagination.page,
        0,
        hasPreviousPage
      );
    }

    const groupById = new Map(groups.map((group) => [group.id, group]));
    const exhibitorGroupIds = groups
      .filter((group) => group.type === ParticipantGroupType.EXHIBITORS)
      .map((group) => group.id);
    const sponsorGroupIds = groups
      .filter((group) => group.type === ParticipantGroupType.SPONSORS)
      .map((group) => group.id);
    const searchTerm = normalizeCompanySearchQuery(query);
    const window = searchTerm
      ? await loadCompanySearchWindow({
          eventId,
          lang,
          query: searchTerm,
          exhibitorGroupIds,
          sponsorGroupIds,
          page: pagination.page,
          take: pagination.take,
          cursor: safeCursor,
        })
      : await loadMergedCompanyWindow({
          exhibitorWhere: companyCollectionWhere(eventId, exhibitorGroupIds),
          sponsorWhere: companyCollectionWhere(eventId, sponsorGroupIds),
          page: pagination.page,
          take: pagination.take,
          cursor: safeCursor,
        });

    if (!window.ids.length) {
      return emptyCompanyListPage<CompanyParticipantItem>(
        pagination.page,
        window.total,
        hasPreviousPage
      );
    }

    const exhibitorIds = window.ids.filter((id) => window.exhibitorIds.has(id));
    const sponsorIds = window.ids.filter((id) => !window.exhibitorIds.has(id));
    const listInclude = companyListInclude(eventId);

    const [exhibitors, sponsors, network] = await Promise.all([
      exhibitorIds.length
        ? reportingPrisma.exhibitor.findMany({
            where: { id: { in: exhibitorIds }, eventId },
            include: listInclude,
          })
        : Promise.resolve([]),
      sponsorIds.length
        ? reportingPrisma.sponsor.findMany({
            where: { id: { in: sponsorIds }, eventId },
            include: listInclude,
          })
        : Promise.resolve([]),
      session?.id
        ? loadCompanyNetworkState(eventId, session.id, window.ids)
        : Promise.resolve(null),
    ]);

    const exhibitorById = new Map(exhibitors.map((row) => [row.id, row]));
    const sponsorById = new Map(sponsors.map((row) => [row.id, row]));
    const data: CompanyParticipantItem[] = [];

    for (const id of window.ids) {
      const exhibitor = exhibitorById.get(id);
      const company = exhibitor ?? sponsorById.get(id);
      if (!company) continue;
      const group = company.participantGroupId
        ? groupById.get(company.participantGroupId)
        : undefined;
      const item = mapCompanyToParticipantItem({
        company,
        group: {
          type: exhibitor
            ? ParticipantGroupType.EXHIBITORS
            : ParticipantGroupType.SPONSORS,
          color: group?.color,
          translations: group?.translations,
        },
        profile: pickCompanyContactProfile(
          company.team,
          company.email,
          eventId
        ),
        lang,
      });
      if (network) {
        item.isBookmarked = network.bookmarkedIds.has(item.id);
        item.connectionRequests = network.connectionStatus.get(item.id) ?? null;
      }
      data.push(item);
    }

    return companyListPage({
      data,
      total: window.total,
      page: pagination.page,
      hasNextPage: window.hasNextPage,
      hasPreviousPage,
      nextCursor: window.nextCursor,
    });
  } catch (error) {
    console.error(error);
    throw new Error("Failed to get companies");
  }
};

const companyTeamSelect = (eventId: string) => ({
  select: {
    id: true,
    name: true,
    email: true,
    image: true,
    profiles: {
      where: { eventId },
      orderBy: { createdAt: "desc" as const },
      take: 1,
      select: {
        id: true,
        eventId: true,
        name: true,
        email: true,
        phoneNumber: true,
        countryCode: true,
        jobTitle: true,
        company: true,
        country: true,
        city: true,
        state: true,
        gender: true,
        age: true,
        bio: true,
        image: true,
        socialMedia: true,
        userId: true,
        extraData: true,
        createdAt: true,
        updatedAt: true,
      },
    },
  },
});

const companyDetailSelect = (eventId: string, lang: string) => ({
  id: true,
  eventId: true,
  email: true,
  phone: true,
  logo: true,
  website: true,
  facebook: true,
  linkedin: true,
  x: true,
  instagram: true,
  location: true,
  boothName: true,
  tags: true,
  createdAt: true,
  updatedAt: true,
  teamIds: true,
  translations: {
    select: {
      name: true,
      bio: true,
      tags: true,
      language: { select: { code: true } },
    },
  },
  networking: {
    take: 1,
    orderBy: { createdAt: "desc" as const },
  },
  participantGroup: {
    select: {
      type: true,
      color: true,
      translations: {
        where: { language: { code: lang } },
        select: { name: true },
        take: 1,
      },
    },
  },
  team: companyTeamSelect(eventId),
});

const productDetailSelect = {
  id: true,
  image: true,
  link: true,
  translations: {
    select: {
      name: true,
      description: true,
      language: { select: { code: true } },
    },
  },
} as const;

const documentDetailSelect = {
  id: true,
  type: true,
  translations: {
    select: {
      name: true,
      url: true,
      language: { select: { code: true } },
    },
  },
} as const;

const videoDetailSelect = {
  id: true,
  translations: {
    select: {
      name: true,
      url: true,
      language: { select: { code: true } },
    },
  },
} as const;

const attachCompanyNetworkState = async (
  item: CompanyParticipantItem,
  eventId: string,
  session?: User
) => {
  if (!session?.id) return item;
  const [bookmarkedIds, connectionRequests] = await Promise.all([
    getBookmarkedEntityIds(session.id, eventId, EntityModel.PROFILE, [item.id]),
    prisma.connectionRequest.findMany({
      where: {
        eventId,
        status: { not: ConnectionStatus.REJECTED },
        OR: [
          { senderId: session.id, receiverId: item.id },
          { receiverId: session.id, senderId: item.id },
        ],
      },
      select: { senderId: true, receiverId: true, status: true },
      orderBy: { createdAt: "desc" },
      take: 1,
    }),
  ]);
  item.isBookmarked = bookmarkedIds.has(item.id);
  item.connectionRequests = connectionRequests[0]?.status ?? null;
  return item;
};

export const getCompanyByEventAndId = async (
  eventId: string,
  companyId: string,
  lang: string = "en",
  session?: User,
  typeHint?: ParticipantGroupType
) => {
  try {
    const select = companyDetailSelect(eventId, lang);
    const lookExhibitor =
      !typeHint || typeHint === ParticipantGroupType.EXHIBITORS;
    const lookSponsor = !typeHint || typeHint === ParticipantGroupType.SPONSORS;

    const [exhibitor, sponsor] = await Promise.all([
      lookExhibitor
        ? prisma.exhibitor.findUnique({
            where: { id: companyId },
            select,
          })
        : Promise.resolve(null),
      lookSponsor
        ? prisma.sponsor.findUnique({
            where: { id: companyId },
            select,
          })
        : Promise.resolve(null),
    ]);

    const record =
      exhibitor?.eventId === eventId
        ? { company: exhibitor, type: ParticipantGroupType.EXHIBITORS }
        : sponsor?.eventId === eventId
        ? { company: sponsor, type: ParticipantGroupType.SPONSORS }
        : null;

    if (!record) return null;

    const [products, documents, videos] = await Promise.all([
      prisma.product.findMany({
        where: { entityId: record.company.id },
        select: productDetailSelect,
        orderBy: { createdAt: "desc" },
      }),
      prisma.document.findMany({
        where: { entityId: record.company.id },
        select: documentDetailSelect,
        orderBy: { createdAt: "desc" },
      }),
      prisma.video.findMany({
        where: { entityId: record.company.id },
        select: videoDetailSelect,
        orderBy: { createdAt: "desc" },
      }),
    ]);

    const profile = pickCompanyContactProfile(
      record.company.team,
      record.company.email,
      eventId
    );

    const item = mapCompanyToParticipantItem({
      company: record.company,
      group: {
        type: record.type,
        color: record.company.participantGroup?.color,
        translations: record.company.participantGroup?.translations,
      },
      profile,
      lang,
    });

    item.team = mapCompanyTeamMembers(record.company.team, eventId);
    item.products = mapCompanyProducts(products, lang);
    item.documents = mapCompanyDocuments(documents, lang);
    item.videos = mapCompanyVideos(videos, lang);

    return attachCompanyNetworkState(item, eventId, session);
  } catch (error) {
    console.error(error);
    throw new Error("Failed to get company");
  }
};
